import {
  DIRECTION_ASC,
  DIRECTION_DESC,
  SORTS,
} from '../../../../../enum/sort.enum';
import {SortOption} from '../types/VolPerfComponent.types';
import {VolumePerformanceReportState} from '../types/volumePerformanceReport.types';

/**
 * TAB NAME CONSTANTS
 */
export const TAB_NAMES = {
  ORDERS: 'orders',
  SCHEDULED: 'scheduled',
} as const;

/**
 * PERIOD CONSTANTS
 */
export const PERIODS = {
  LAST_MONTH: '0',
  CURRENT_MONTH: '1',
  NEXT_MONTH: '2',
} as const;

/**
 * ORDER CATEGORY CONSTANTS
 */
export const ORDER_CATEGORIES = {
  ORDERS: 'orders',
  TEMPLATES: 'templates',
} as const;

/**
 * FILTER VALUE CONSTANTS
 */
export const FILTER_VALUES = {
  ALL: 'all',
  PREMIER_PLUS: 'premierPlus',
  ON_DEMAND: 'onDemand',
  ORDERS: 'orders',
  RETURNS: 'returns',
  CANCELLATIONS: 'cancelled',
  SCHEDULED_PREMIERE: 'active',
  CANCELLED_SCHEDULED_PREMIERE: 'inactive',
  CLIENT: 'client',
  COACH: 'coach',
} as const;

/**
 * USER CARD CONSTANTS
 */
export const USER_CARD = {
  ROLES: {
    COACH: 'Coach',
    CLIENT: 'Client',
  },
  ORDER_TYPES: {
    PREMIER_PLUS: 'Premier+',
    ON_DEMAND: 'on-Demand',
  },
  STATUS: {
    RETURNED: 'Returned',
    SCHEDULED: 'Scheduled',
    CANCELLED: 'Cancelled',
    ORDER: 'Order',
    SHIPPED: 'Shipped',
  },
  ORDER_ID_PREFIX: {
    RET: 'RET-',
    SCH: 'SCH-',
    CANC: 'CANC-',
    ORD: 'ORD-',
  },
  LABELS: {
    ORDER_DATE: 'Order Date',
    SCHEDULED_FOR: 'Scheduled for',
    CANCEL_DATE: 'Cancel Date',
    CANCELLED_SCHEDULED: 'Cancelled Scheduled',
    LAST_PREMIER_ORDER: 'Last Premier+ Order',
    RETURN_DATE: 'Return Date',
    NEXT_PREMIER_ORDER: 'Next Premier+ Order',
    QV: 'QV',
    SUB_TOTAL: 'Sub-total (consumables)',
    TOTAL: 'Total',
  },
} as const;

/**
 * RANGE FILTER CONSTANTS
 */
export const RANGE_FILTER = {
  IDS: {
    ABOVE: 'above',
    BELOW: 'below',
    BETWEEN: 'between',
  },
  KEYS: {
    ABOVE_VALUE: 'aboveValue',
    BELOW_VALUE: 'belowValue',
  },
  PLACEHOLDERS: {
    RANGE_EG: 'e.g., 5000',
    MIN: 'Min',
    MAX: 'Max',
  },
  ICONS: {
    CHECKED: 'checkbox-selected',
    UNCHECKED: 'checkbox-unselected',
  },
} as const;

/**
 * MONTHS DATA
 * Defines the display labels for the month filter options.
 */
export const MONTHS_DATA = {
  last_month: 'Last Month',
  current_month: 'Current Month',
  next_month: 'Next Month',
};

/**
 * DEFAULT FILTER STATE
 * Explicitly defines the initial values for all filter fields.
 */
export const DEFAULT_VOLUME_PERFORMANCE_FILTERS = {
  isFrontline: true,
  orderCategory: ORDER_CATEGORIES.ORDERS,
  period: PERIODS.CURRENT_MONTH,
  accountType: undefined,
  orderType: undefined,
  orderStatus: undefined,
  isNewClient: undefined,
  consSubtotal: undefined,
  qv: undefined,
};

/**
 * INITIAL REDUX STATE
 * The base state used when the Volume Performance report is initialized or reset.
 */
export const INITIAL_VOLUME_PERFORMANCE_STATE: VolumePerformanceReportState = {
  isListLoading: false,
  isFetchingNextPage: false,
  isEndReached: false,
  data: [],
  resultsCount: 0,
  filters: DEFAULT_VOLUME_PERFORMANCE_FILTERS,
  sort: {
    sortBy: 'orderDate',
    sortOrder: DIRECTION_DESC,
  },
};

export const SORT_OPTIONS: SortOption[] = [
  {
    id: `orderDate-${DIRECTION_DESC}`,
    label: SORTS.profileOrderDateDesc.label,
    sortBy: 'orderDate',
    sortOrder: DIRECTION_DESC,
  },
  {
    id: `orderDate-${DIRECTION_ASC}`,
    label: SORTS.profileOrderDateAsc.label,
    sortBy: 'orderDate',
    sortOrder: DIRECTION_ASC,
  },
  {
    id: `amount-${DIRECTION_DESC}`,
    label: SORTS.profileAmountDesc.label,
    sortBy: 'amount',
    sortOrder: DIRECTION_DESC,
  },
  {
    id: `amount-${DIRECTION_ASC}`,
    label: SORTS.profileAmountAsc.label,
    sortBy: 'amount',
    sortOrder: DIRECTION_ASC,
  },
];

export const sanitize = (value: string = '') =>
  value
    ?.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
